import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { listFrames } from './db';

const TARGET_FRAMES = 240; // 8 s of drawing @ 30 fps
const FPS = 30;
const HOLD_FRAMES = 60; // 2 s hold @ 30 fps
const FRAME_DURATION_US = 1_000_000 / FPS;
const MAX_DIM = 1920;
const CODEC_CANDIDATES = ['avc1.42E01F', 'avc1.4D401F', 'avc1.640028'];

const pickIndices = (n, target = TARGET_FRAMES) => {
  if (n <= 0) return [];
  if (target <= 1) return [0];
  // Always produce exactly `target` indices. When n < target, source frames
  // repeat (slow timelapse); when n > target, we sample evenly (fast timelapse).
  return Array.from({ length: target }, (_, i) =>
    Math.round((i * (n - 1)) / (target - 1))
  );
};

const fitDims = (w, h) => {
  const longest = Math.max(w, h);
  if (longest <= MAX_DIM) return { w: align16(w), h: align16(h) };
  const s = MAX_DIM / longest;
  return { w: align16(Math.round(w * s)), h: align16(Math.round(h * s)) };
};

const align16 = (n) => Math.max(16, Math.floor(n / 16) * 16);

const pickCodec = async (width, height) => {
  if (typeof window === 'undefined' || !('VideoEncoder' in window)) return null;
  for (const codec of CODEC_CANDIDATES) {
    try {
      const cfg = {
        codec,
        width,
        height,
        bitrate: 4_000_000,
        framerate: FPS,
        latencyMode: 'realtime',
        bitrateMode: 'constant',
      };
      const res = await window.VideoEncoder.isConfigSupported(cfg);
      if (res && res.supported) return res.config || cfg;
    } catch {
      // try next
    }
  }
  return null;
};

export const isExportSupported = () =>
  typeof window !== 'undefined' &&
  'VideoEncoder' in window &&
  'VideoFrame' in window &&
  typeof createImageBitmap === 'function';

export const runTimelapseExport = async ({ guid, date, onProgress }) => {
  if (!isExportSupported()) {
    throw new Error('Video export requires iPadOS 16.4 or newer.');
  }

  const frames = await listFrames(guid, date);
  if (!frames.length) {
    throw new Error('No frames captured yet — draw something first.');
  }

  const indices = pickIndices(frames.length, TARGET_FRAMES);
  const totalOutputFrames = indices.length + HOLD_FRAMES;

  const srcW = frames[0].w || 1024;
  const srcH = frames[0].h || 1024;
  const { w: outW, h: outH } = fitDims(srcW, srcH);

  const supportedConfig = await pickCodec(outW, outH);
  if (!supportedConfig) {
    throw new Error('No supported H.264 profile on this device.');
  }
  const codec = supportedConfig.codec;

  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: {
      codec: 'avc',
      width: outW,
      height: outH,
      frameRate: FPS,
    },
    fastStart: 'in-memory',
  });

  let encoderError = null;
  const encoder = new window.VideoEncoder({
    output: (chunk, meta) => {
      // Safari's H.264 encoder doesn't always populate chunk.duration.
      // Use addVideoChunkRaw so we can supply a fallback duration ourselves.
      const data = new Uint8Array(chunk.byteLength);
      chunk.copyTo(data);
      const duration = Number.isFinite(chunk.duration) && chunk.duration > 0
        ? chunk.duration
        : Math.round(FRAME_DURATION_US);
      muxer.addVideoChunkRaw(data, chunk.type, chunk.timestamp, duration, meta);
    },
    error: (e) => {
      encoderError = e;
      console.error('[timelapse] encoder error', e);
    },
  });

  try {
    encoder.configure(supportedConfig);
  } catch (e) {
    throw new Error(`Encoder configure threw: ${e.message || e} (codec=${codec}, ${outW}x${outH})`);
  }

  // Let any async configure error surface before we start encoding.
  for (let i = 0; i < 15; i++) {
    if (encoderError || encoder.state !== 'configured') break;
    await new Promise((r) => setTimeout(r, 20));
  }
  const describeEncoderError = (label) => {
    const e = encoderError;
    const msg = (e && (e.message || e.name)) || (e ? String(e) : 'no error message');
    return `${label}: ${msg} (codec=${codec}, ${outW}x${outH}, state=${encoder.state})`;
  };
  if (encoderError) {
    throw new Error(describeEncoderError('Encoder config rejected'));
  }
  if (encoder.state !== 'configured') {
    throw new Error(`Encoder not configured (state=${encoder.state}, codec=${codec}, ${outW}x${outH})`);
  }

  // Use a DOM canvas (not OffscreenCanvas) — iPadOS Safari's VideoFrame
  // pipeline is more reliable feeding from a regular <canvas> element.
  // alpha: false produces opaque RGB; VideoToolbox's RGBA→YUV path can fail
  // on transparent pixels.
  const offscreen = document.createElement('canvas');
  offscreen.width = outW;
  offscreen.height = outH;
  const ctx = offscreen.getContext('2d', { alpha: false });
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);

  const waitForQueue = async () => {
    while (encoder.encodeQueueSize > 6) {
      await new Promise((r) => setTimeout(r, 0));
    }
  };

  let lastBitmap = null;

  for (let i = 0; i < indices.length; i++) {
    if (encoderError) throw new Error(describeEncoderError('Encoder failed mid-encode'));
    if (encoder.state !== 'configured') {
      throw new Error(`Encoder dropped out of configured state at frame ${i} (state=${encoder.state}, codec=${codec})`);
    }
    const rec = frames[indices[i]];
    const bitmap = await createImageBitmap(rec.blob);
    // Fit-with-letterbox: if a frame's intrinsic dims differ from outW/outH
    // (e.g. mid-session ratio change before purge), show white bars instead
    // of silently stretching. Same-ratio sessions hit s=1, dx=dy=0.
    const bmpW = bitmap.width;
    const bmpH = bitmap.height;
    const s = Math.min(outW / bmpW, outH / bmpH);
    const drawW = Math.round(bmpW * s);
    const drawH = Math.round(bmpH * s);
    const dx = Math.round((outW - drawW) / 2);
    const dy = Math.round((outH - drawH) / 2);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(bitmap, dx, dy, drawW, drawH);

    const frame = new window.VideoFrame(offscreen, {
      timestamp: Math.round(i * FRAME_DURATION_US),
      duration: Math.round(FRAME_DURATION_US),
    });
    await waitForQueue();
    try {
      encoder.encode(frame, { keyFrame: i % FPS === 0 });
    } catch (e) {
      frame.close();
      throw new Error(`encode() threw at frame ${i}: ${e.message || e} | encoderError=${encoderError ? (encoderError.message || encoderError.name || String(encoderError)) : 'none'} | state=${encoder.state}`);
    }
    frame.close();

    if (lastBitmap) lastBitmap.close();
    lastBitmap = bitmap;

    if (onProgress) onProgress(i + 1, totalOutputFrames);
  }

  if (lastBitmap) {
    const bmpW = lastBitmap.width;
    const bmpH = lastBitmap.height;
    const s = Math.min(outW / bmpW, outH / bmpH);
    const drawW = Math.round(bmpW * s);
    const drawH = Math.round(bmpH * s);
    const dx = Math.round((outW - drawW) / 2);
    const dy = Math.round((outH - drawH) / 2);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(lastBitmap, dx, dy, drawW, drawH);
    for (let h = 0; h < HOLD_FRAMES; h++) {
      const t = indices.length + h;
      const frame = new window.VideoFrame(offscreen, {
        timestamp: Math.round(t * FRAME_DURATION_US),
        duration: Math.round(FRAME_DURATION_US),
      });
      await waitForQueue();
      encoder.encode(frame, { keyFrame: h === HOLD_FRAMES - 1 });
      frame.close();
      if (onProgress) onProgress(t + 1, totalOutputFrames);
    }
    lastBitmap.close();
  }

  await encoder.flush();
  encoder.close();
  muxer.finalize();

  return new Blob([muxer.target.buffer], { type: 'video/mp4' });
};
